/**
 * Chargement de données : un état unique `{ data, error, loading }` plus un `reload()`.
 *
 * Pas de bibliothèque de cache : l'application est petite et une couche de plus coûterait
 * plus qu'elle ne rapporte. En revanche, l'annulation est réelle — une réponse arrivée après
 * un changement de projet ne doit jamais écraser l'état courant.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param {(signal: AbortSignal) => Promise<any>} loader
 * @param {readonly unknown[]} deps
 */
export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [tick, setTick] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    loaderRef
      .current(controller.signal)
      .then((data) => {
        if (alive) setState({ data, error: null, loading: false });
      })
      .catch((error) => {
        if (alive && error.name !== 'AbortError') setState({ data: null, error, loading: false });
      });

    return () => {
      alive = false;
      controller.abort();
    };
    // Dépendances volontairement dynamiques : c'est l'appelant qui décide de ce qui
    // déclenche un rechargement, `loader` étant lu via une réf pour ne pas en faire partie.
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  /** Mise à jour optimiste locale — évite un aller-retour serveur sur un glisser-déposer. */
  const patch = useCallback((updater) => {
    setState((previous) => ({ ...previous, data: updater(previous.data) }));
  }, []);

  return { ...state, reload, patch };
}
